import axios from "axios";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { Button, Modal } from "react-bootstrap";
import { Route, Routes, useLocation } from "react-router";
import { BrowserRouter, useSearchParams } from "react-router-dom";
import useXoltarNavigation from "../../hooks/useXoltarNav";
import WebsocketEventer from "../../utils/websockets/WebsocketEventer";
import { WebSocketContext } from "../Context/WebSocketContext";
import ErrorPage from "../../Pages/Error/Error";
import { useAppDispatch, useAppSelector } from "../../redux-stores/hooks/hooks";
import { setConversation } from "../../redux-stores/reducers/ConversationState";
import {
  selectSiteFile,
  selectSiteImages,
  setImages,
  setIsOneTime,
  setLoadedSettings,
  setOneTimeID,
  setPages,
  setQuestionsFile,
  setSiteSourceFile,
} from "../../redux-stores/reducers/PageState";
import {
  setExperiment,
  setRunProlific,
  setUser as setProlifcUser,
} from "../../redux-stores/reducers/ProlificExperiments";
import { setAnswer } from "../../redux-stores/reducers/QuestionerState";
import { addToast } from "../../redux-stores/reducers/ToastState";
import ToastHandler from "../Toast/ToastHandler";
import RouterMap, { KnownPages } from "./RouterMap";

const AlertOldEventExsists = forwardRef<
  unknown,
  { onClose?: Function; onAccept?: Function }
>((props, ref) => {
  const [show, setShow] = useState(false);
  useImperativeHandle(ref, () => setShow);
  const handleClose = () => {
    setShow(false);
  };
  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Already waiting</Modal.Title>
        </Modal.Header>
        <Modal.Body>I see an agent is already getting ready for you</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => props.onClose && props.onClose()}
          >
            Restart
          </Button>
          <Button
            variant="primary"
            onClick={() => props.onAccept && props.onAccept()}
          >
            Continue to video chat
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

function StartPage(props: { onLoad: Function }) {
  const dispatch = useAppDispatch();
  const site_file_local = useAppSelector(selectSiteFile);
  const navigation = useXoltarNavigation();
  const [params] = useSearchParams();
  const location = useLocation();

  useEffect(() => {
    params.forEach((value, key) => {
      if (key === "conversation") {
        dispatch(setConversation(value));
      } else if (key.toLowerCase() === "prolificid")
        dispatch(
          setProlifcUser({ prolificId: value, experiments: [], metadata: {} })
        );
      else if (key.toLowerCase() === "prolificexp")
        dispatch(setExperiment(value));
      else if (key.toLowerCase() === "duid") dispatch(setOneTimeID(value));
      dispatch(setAnswer({ key, value }));
    });

    const siteDownload =
      params.get("site") !== null
        ? `${params.get("site")}.json`
        : site_file_local;
    if (!siteDownload) {
      navigation("Error", new URLSearchParams({ type: "Missing" }));
      return;
    }
    dispatch(setSiteSourceFile(siteDownload));
    axios
      .get(`/static/sites/${siteDownload}`)
      .then((res) => {
        const site_file = res.data.site_file as string;
        dispatch(setQuestionsFile(res.data.questionnaire));
        if (res.data.images) {
          dispatch(setImages(res.data.images));
        }
        if (res.data.title) {
          document.title = res.data.title;
        }
        if (res.data.prolific) dispatch(setRunProlific(true));
        else dispatch(setRunProlific(false));
        if (res.data.demo) dispatch(setIsOneTime(true));
        else dispatch(setIsOneTime(false));
        if (res.data.conversation && typeof res.data.conversation === "string")
          dispatch(setConversation(res.data.conversation));
        axios
          .get(`/static/configs/${site_file}`)
          .then((res) => {
            dispatch(setPages(res.data));
            dispatch(setLoadedSettings(res.data));
            props.onLoad();
          })
          .finally(() => {
            const path = location.pathname;
            Object.entries(RouterMap).forEach(([key, value]) => {
              if (value.routes.find((p) => p === path)) {
                navigation(key as KnownPages);
              }
            });
          });
      })
      .catch(() =>
        navigation("Error", new URLSearchParams({ type: "Missing" }))
      );
    return () => {};
    // eslint-disable-next-line
  }, [params]);

  return <></>;
}

function Router() {
  const [loadingPrivacy, setLoadingPrivacy] = React.useState(true);
  const images = useAppSelector(selectSiteImages);
  const setShow = useRef<(a: boolean) => boolean>();
  const dispatch = useAppDispatch();
  const webSocket = new WebsocketEventer();
  useEffect(() => {
    console.log(
      `${process.env.REACT_APP_NAME} ${process.env.REACT_APP_VERSION}`
    );
  }, []);
  return (
    <>
      <AlertOldEventExsists
        ref={setShow}
        onAccept={() => {}}
        onClose={() => {}}
      />
      <div
        className="toast-handler"
        style={{
          display: "flex",
          flexDirection: "row-reverse",
          paddingTop: "1rem",
          paddingBottom: "1rem",
          paddingLeft: "1rem",
          position: "absolute",
          right: 0,
          top: "0",
          width: "100%",
        }}
      >
        <ToastHandler />
      </div>
      {images && images.left ? (
        <div
          style={{
            display: "flex",
            paddingTop: "1rem",
            paddingBottom: "1rem",
            paddingLeft: "1rem",
            position: "absolute",
            left: 0,
            top: "0",
          }}
        >
          <img
            alt="Left top logo"
            style={{
              paddingBottom: "1rem",
              marginRight: "auto",
              marginTop: "auto",
              marginBottom: "auto",
              height: "10vh",
              maxHeight: "5rem",
              aspectRatio: "9/2",
              zIndex: 9999,
            }}
            onClick={() =>
              dispatch(
                addToast({
                  bg: "Primary",
                  body: "Hello",
                  header: "World",
                  duration: 3000,
                  timestamp: Date.now(),
                })
              )
            }
            src={images?.left}
          />
        </div>
      ) : null}
      <div style={{ height: "100%", display: "flex" }}>
        <WebSocketContext.Provider value={webSocket}>
          <BrowserRouter>
            <Routes>
              {!loadingPrivacy ? (
                Object.entries(RouterMap).map((entry) => {
                  return entry[1].routes.map((route) => {
                    return (
                      <Route
                        key={`route-${route}`}
                        path={route}
                        element={entry[1].element}
                      />
                    );
                  });
                })
              ) : (
                <>
                  <Route
                    path={RouterMap.Error.default}
                    element={<ErrorPage />}
                  />
                  <Route
                    path="*"
                    element={
                      <StartPage
                        onLoad={() => {
                          setLoadingPrivacy(false);
                        }}
                      />
                    }
                  />
                </>
              )}
            </Routes>
          </BrowserRouter>
        </WebSocketContext.Provider>
      </div>
      <div
        style={{
          display: "flex",
          position: "absolute",
          right: "1rem",
          bottom: "0",
        }}
      ></div>
    </>
  );
}

export default Router;
